import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import AuthShell from "../../components/NewLogin/AuthShell";
import AuthFormWrapper from "../../components/NewLogin/AuthFormWrapper";
import MicrosoftIcon from "../../components/NewLogin/MicrosoftIcon";
import OktaIcon from "../../components/NewLogin/OktaIcon";
import { useTranslation } from "react-i18next";
import images from "../../constant/images";
import { AnimatePresence, motion } from "framer-motion";
import { pageTransition, pageVariants } from "./Animation";

const FONT = "'Euclid Circular B', sans-serif";
const FONT_REGULAR = "'Euclid Circular Light', sans-serif";


const labelSx = {
  fontFamily: FONT_REGULAR,
  fontSize: "1.143rem",
  fontWeight: 300,
  lineHeight: "1.429rem",
  color: "#474849",
  display: "block",
  mb: "0.271rem",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: "3.429rem",
    borderRadius: "0.286rem",
    fontFamily: FONT,
    fontSize: "1.048rem",
    fontWeight: 300,
    lineHeight: "1.1233rem",
    color: "#525252",
    "& fieldset": { borderColor: "#D0D4DA" },
    "&:hover fieldset": { borderColor: "#07080B" },
    "&.Mui-focused fieldset": { borderColor: "#07080B", borderWidth: "0.095rem" },
  },
  "& input::placeholder": { color: "#525252", opacity: 1 },
  "& .MuiFormHelperText-root": { fontFamily: FONT, fontSize: "0.952rem" },
  "& .MuiOutlinedInput-input": {
    WebkitBoxShadow: "0 0 0 30px white inset !important", // autofill fix
  },
};

/**
 * LoginPage
 *
 * Props:
 *   loginForm        – { email, password, showPassword }
 *   errors           – { email?, password? }
 *   isSubmitting     – boolean
 *   onFieldChange    – (field) => (event) => void
 *   onTogglePassword – () => void
 *   onSubmit         – () => void
 *   onOktaLogin      – () => void
 *   onMicrosoftLogin – () => void
 *   onForgotPassword – () => void
 */
const LoginPage = ({
  loginForm,
  errors,
  isLoginDisabled,
  isSubmitting,
  onFieldChange,
  onTogglePassword,
  onSubmit,
  onOktaLogin,
  onMicrosoftLogin,
  onForgotPassword,
}) => {
  const { t } = useTranslation();

  // purely visual hover state
  const [hoveredAuth, setHoveredAuth] = useState(null); // "okta" | "microsoft" | null

  // actual disable state
  const isLocked = isSubmitting || isLoginDisabled;

  const dimOpacity = hoveredAuth ? 0.35 : 1;

  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    const checkMac = () => {
      // ✅ Modern API (Chromium-based browsers)
      if (navigator.userAgentData?.platform) {
        return navigator.userAgentData.platform.toLowerCase().includes("mac");
      }

      // ✅ Fallback (Safari, Firefox, older browsers)
      return navigator.userAgent.toLowerCase().includes("mac");
    };

    setIsMac(checkMac());
  }, []);

  return (
    <AuthShell isLoading={false} isMac={isMac}>
      <AuthFormWrapper maxWidth={"28.571rem"}>
        <AnimatePresence mode="wait">
          <motion.div
            key="login"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ height: isMac ? "75%" : "100%" }}
            transition={pageTransition}
          >
            <Box sx={{
              height: "100%",
              display: "flex",
              flexFlow: "column",
              alignItems: "center",
              justifyContent: "space-around"
            }}>
              {/* Logo + Header text */}
              <Stack alignItems="center" spacing="0.476rem" sx={{ mb: "1.857rem" }} gap={"1.429rem"}>
                <Box component="img" src={images.SCAI} />

                {/* Header text */}
                <Stack alignItems="center" spacing="0.276rem" width={"100%"}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: FONT,
                      fontSize: "1.8rem",
                      fontWeight: 400,
                      lineHeight: "1.857rem",
                      color: "#07080B",
                      textAlign: "center",
                    }}
                  >
                    {t("Unlock_Intelligent_Planning")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT_REGULAR,
                      fontSize: "1.143rem",
                      fontWeight: 300,
                      lineHeight: "1.714rem",
                      color: "#474849",
                      textAlign: "center",
                    }}
                  >
                    {t("Enter_your_details_below_to_proceed")}
                  </Typography>
                </Stack>
              </Stack>

              {/* Form */}
              <Box sx={{ width: "100%" }}>
                <Stack
                  spacing="0.952rem"
                  sx={{
                    mb: "0.505rem",
                    opacity: hoveredAuth ? dimOpacity : 1,
                    transition: "opacity 0.01s ease",
                  }}
                >
                  <Box>
                    <Typography component="label" htmlFor="login-email" sx={labelSx}>
                      {t("Email")}
                    </Typography>
                    <TextField
                      id="login-email"
                      fullWidth
                      size="small"
                      placeholder={t("enterEmailAddressPlaceholder")}
                      value={loginForm.email}
                      onChange={onFieldChange("email")}
                      error={!!errors}
                      helperText={t(errors)}
                      autoComplete="email"
                      // disabled={isLocked}
                      slotProps={{ input: { "aria-label": "Email address" } }}
                      sx={inputSx}
                    />
                  </Box>

                  <Box>
                    <Typography component="label" htmlFor="login-password" sx={labelSx}>
                      {t("Password")}
                    </Typography>
                    <TextField
                      id="login-password"
                      fullWidth
                      size="small"
                      type={loginForm.showPassword ? "text" : "password"}
                      placeholder={t("passwordPlaceholder")}
                      value={loginForm.password}
                      onChange={onFieldChange("password")}
                      autoComplete="current-password"
                      // disabled={isLocked}
                      sx={inputSx}
                      slotProps={{
                        input: {
                          "aria-label": "Password",
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={onTogglePassword}
                                edge="end"
                                size="small"
                                aria-label={loginForm.showPassword ? "Hide password" : "Show password"}
                                // disabled={isLocked}
                                sx={{ color: "#666D80", mr: "-0.381rem" }}
                              >
                                {loginForm.showPassword ? (
                                  <Box component={"img"} src={images.LockOpenPasswordIcon} />
                                ) : (
                                  <Box component={"img"} src={images.LockedpasswordIcon} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </Stack>

                <Box sx={{ textAlign: "right", mb: "1.2rem", opacity: hoveredAuth ? dimOpacity : 1 }}>
                  <Link
                    component="button"
                    onClick={onForgotPassword}
                    underline="none"
                    sx={{
                      fontFamily: FONT,
                      fontSize: "1.048rem",
                      fontWeight: 300,
                      lineHeight: "1.333rem",
                      color: "#0166FF",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {t("forgotPassword")}
                  </Link>
                </Box>
              </Box>

              {/* Buttons */}
              <Stack spacing="1rem" sx={{ mb: "1.429rem", width: "100%" }}>
                {/* Primary login */}
                <Box
                  sx={{
                    border: "0.095rem solid #07080B",
                    borderRadius: "0.190rem",
                    padding: "1px",
                    boxSizing: "border-box",
                    opacity: hoveredAuth ? dimOpacity : 1,
                    transition: "opacity 0.01s ease",
                    "&:hover": { border: "0.095rem solid rgb(233, 79, 28) !important" }
                  }}
                >
                  <Button
                    fullWidth
                    onClick={() => onSubmit(loginForm.email, loginForm.password)}
                    aria-label="Launch Workspace"
                    //disabled={isLocked}
                    sx={{
                      fontFamily: FONT_REGULAR,
                      fontSize: "1.043rem",
                      fontWeight: 300,
                      lineHeight: "1.524rem",
                      color: hoveredAuth ? "#07080B" : "#F5F5F5",
                      backgroundColor: !hoveredAuth ? "#07080B" : "#F5F5F5",
                      borderRadius: "0.190rem",
                      height: "3rem",
                      padding: "0.952rem 2.286rem",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "rgb(233, 79, 28)", color: "#ffffff" },
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={"1.524rem"} sx={{ color: "#fff" }} /> : t("LaunchWorkspace")}
                  </Button>
                </Box>

                {/* Okta */}
                <Box
                  onMouseEnter={() => setHoveredAuth("okta")}
                  onMouseLeave={() => setHoveredAuth(null)}
                  sx={{
                    border: "0.095rem solid #07080B",
                    borderRadius: "0.190rem",
                    padding: "1px",
                    boxSizing: "border-box",
                    opacity: hoveredAuth && hoveredAuth !== "okta" ? dimOpacity : 1,
                    transition: "opacity 0.01s ease",
                  }}
                >
                  <Button
                    fullWidth
                    onClick={onOktaLogin}
                    aria-label="Sign in with Okta"
                    //disabled={isLocked}
                    startIcon={<OktaIcon size={"1.524rem"} />}
                    sx={{
                      fontFamily: FONT_REGULAR,
                      fontSize: "1.043rem",
                      fontWeight: 300,
                      lineHeight: "1.524rem",
                      color: "#07080B",
                      backgroundColor: "#F5F5F5",
                      borderRadius: "0.190rem",
                      height: "2.9rem",
                      padding: "0.952rem 2.286rem",
                      gap: "0.001rem",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#07080B", color: "#ffffff" },
                    }}
                  >
                    {t("Sign_in_with_okta")}
                  </Button>
                </Box>

                {/* Microsoft */}
                <Box
                  onMouseEnter={() => setHoveredAuth("microsoft")}
                  onMouseLeave={() => setHoveredAuth(null)}
                  sx={{
                    borderRadius: "0.190rem",
                    padding: "1px",
                    marginTop: "0.55rem !important",
                    boxSizing: "border-box",
                    opacity: hoveredAuth && hoveredAuth !== "microsoft" ? dimOpacity : 1,
                    transition: "transform 0.25s ease, margin-top 0.25s ease",
                    transform: "translateY(0)",
                    "&:hover": {
                      border: "0.095rem solid #07080B",
                      //transform: "translateY(1rem)",
                      marginTop: "1rem !important",
                    },
                  }}
                >
                  <Button
                    fullWidth
                    onClick={() => onMicrosoftLogin(loginForm.email)}
                    startIcon={<MicrosoftIcon size={"1.333rem"} />}
                    sx={{
                      fontFamily: FONT_REGULAR,
                      fontSize: "1.043rem",
                      fontWeight: 300,
                      lineHeight: "1.524rem",
                      color: "#07080B",
                      borderRadius: "0.190rem",
                      height: "3rem",
                      padding: "0.952rem 2.286rem",
                      textTransform: "none",
                      transition: "all 0.25s ease",

                      "&:hover": {
                        backgroundColor: "#07080B",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {t("Sign_in_with_Microsoft")}
                  </Button>
                </Box>
              </Stack>

              {/* ── Privacy note ── */}
              <Box sx={{ textAlign: "center", width: "100%" }}>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "0.848rem",
                    fontWeight: 300,
                    lineHeight: "1.238rem",
                    color: "#07080BB2",
                    textWrapStyle: "pretty"
                  }}
                >
                  {t("NOTE_SECURE_ACCESS")}{" "}
                  <Link
                    href="/files/SCAI-Privacy-Policy.docx"
                    download
                    underline="always"
                    sx={{
                      fontFamily: FONT,
                      textDecoration: "underline",
                      fontSize: "0.848rem",
                      fontWeight: 300,
                      color: "#0166FF",
                    }}
                  >
                    {t("Privacy_Policy")}
                  </Link>
                  .
                </Typography>
              </Box>

            </Box>
          </motion.div>
        </AnimatePresence>
      </AuthFormWrapper>
    </AuthShell>
  );
};

export default LoginPage;